import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import type { DocumentActivity } from "../types/project-attachment-workflow.types.ts"
import type {
  ProjectDocument,
  ProjectDocumentBulkResult,
  ProjectDocumentClassificationData,
  ProjectDocumentFilterOptions,
  ProjectDocumentMoveData,
  ProjectDocumentUploadData,
  ProjectDocumentsParams
} from "../types/project-documents.types.ts"

// Documents are `core.ProjectAttachment` rows — the paperwork store, separate
// from the generic attachments the album surfaces read.
const BASE_URL = `/project-management/project-attachments`

const PATHS = {
  getDocuments: `${BASE_URL}/`,
  getFilterOptions: `${BASE_URL}/filter_options/`,
  createDocument: `${BASE_URL}/`,
  document: (id: string) => `${BASE_URL}/${id}/`,
  history: (id: string) => `${BASE_URL}/${id}/history/`,
  restore: (id: string) => `${BASE_URL}/${id}/restore/`,
  changeStatus: (id: string) => `${BASE_URL}/${id}/change-status/`,
  activity: (id: string) => `${BASE_URL}/${id}/activity/`,
  reviewers: (id: string) => `${BASE_URL}/${id}/reviewers/`,
  // Named restore-from-trash, not restore — that name is already taken above
  // by version-history restore (bringing an old version's content back as
  // the current version), an unrelated feature.
  restoreFromTrash: (id: string) => `${BASE_URL}/${id}/restore-from-trash/`,
  permanentDelete: (id: string) => `${BASE_URL}/${id}/permanent-delete/`,
  emptyTrash: `${BASE_URL}/empty-trash/`,
  bulkRestoreFromTrash: `${BASE_URL}/bulk-restore-from-trash/`,
  bulkPermanentDelete: `${BASE_URL}/bulk-permanent-delete/`,
  shareWithCustomer: (id: string) => `${BASE_URL}/${id}/share-with-customer/`,
  unshareFromCustomer: (id: string) =>
    `${BASE_URL}/${id}/unshare-from-customer/`
}

// GET /project-management/project-attachments/
export const getProjectDocuments = async (params: ProjectDocumentsParams) => {
  return request.api.get<
    BasePagination<ProjectDocument>,
    BaseApiResponse<unknown>
  >(PATHS.getDocuments, params)
}

// GET /project-management/project-attachments/:id/
export const getProjectDocument = async (id: string) => {
  return request.api.get<ProjectDocument, BaseApiResponse<unknown>>(
    PATHS.document(id)
  )
}

// GET /project-management/project-attachments/filter_options/
// Answers with a bare object, not the standard details/data envelope.
export const getProjectDocumentFilterOptions = async () => {
  return request.api.get<
    ProjectDocumentFilterOptions,
    BaseApiResponse<unknown>
  >(PATHS.getFilterOptions)
}

// POST /project-management/project-attachments/
export const createProjectDocument = async (
  data: ProjectDocumentUploadData
) => {
  return request.api.post<
    BaseApiResponse<ProjectDocument>,
    BaseApiResponse<unknown>
  >(PATHS.createDocument, data, {
    headers: { "Content-Type": "multipart/form-data" }
  })
}

// PATCH /project-management/project-attachments/:id/ — folder placement only
export const moveProjectDocument = async (
  id: string,
  data: ProjectDocumentMoveData
) => {
  return request.api.patch<
    BaseApiResponse<ProjectDocument>,
    BaseApiResponse<unknown>
  >(PATHS.document(id), data)
}

// PATCH /project-management/project-attachments/:id/ — Document Type / Related Record
export const updateProjectDocumentClassification = async (
  id: string,
  data: ProjectDocumentClassificationData
) => {
  return request.api.patch<
    BaseApiResponse<ProjectDocument>,
    BaseApiResponse<unknown>
  >(PATHS.document(id), data)
}

// POST /project-management/project-attachments/:id/share-with-customer/ —
// a dedicated action, never a bare PATCH, so it always carries its own
// timestamp and activity-log entry.
export const shareDocumentWithCustomer = async (id: string) => {
  return request.api.post<
    BaseApiResponse<ProjectDocument>,
    BaseApiResponse<unknown>
  >(PATHS.shareWithCustomer(id))
}

// POST /project-management/project-attachments/:id/unshare-from-customer/
export const unshareDocumentFromCustomer = async (id: string) => {
  return request.api.post<
    BaseApiResponse<ProjectDocument>,
    BaseApiResponse<unknown>
  >(PATHS.unshareFromCustomer(id))
}

// PATCH /project-management/project-attachments/:id/ — the "stay internal
// even though my folder is customer-visible" override. A plain writable
// field, unlike the pair above.
export const setDocumentCustomerVisibilityExcluded = async (
  id: string,
  excluded: boolean
) => {
  return request.api.patch<
    BaseApiResponse<ProjectDocument>,
    BaseApiResponse<unknown>
  >(PATHS.document(id), { customer_visibility_excluded: excluded })
}

// DELETE /project-management/project-attachments/:id/
export const deleteProjectDocument = async (id: string) => {
  return request.api.delete<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.document(id)
  )
}

// POST /project-management/project-attachments/:id/restore-from-trash/
export const restoreProjectDocumentFromTrash = async (id: string) => {
  return request.api.post<
    BaseApiResponse<ProjectDocument>,
    BaseApiResponse<unknown>
  >(PATHS.restoreFromTrash(id))
}

// POST /project-management/project-attachments/:id/permanent-delete/
export const permanentlyDeleteProjectDocument = async (id: string) => {
  return request.api.post<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.permanentDelete(id)
  )
}

// POST /project-management/project-attachments/empty-trash/?lead= — omit
// `projectId` for Global Trash's company-wide empty, which must send the
// explicit `scope=all` opt-in rather than relying on the param's absence
// (the backend refuses an empty-trash call naming neither).
export const emptyProjectDocumentsTrash = async (projectId?: string) => {
  return request.api.post<
    BaseApiResponse<{ deleted: number }>,
    BaseApiResponse<unknown>
  >(PATHS.emptyTrash, undefined, {
    params: projectId ? { lead: projectId } : { scope: "all" }
  })
}

// POST /project-management/project-attachments/bulk-restore-from-trash/
export const bulkRestoreProjectDocumentsFromTrash = async (ids: string[]) => {
  return request.api.post<ProjectDocumentBulkResult, BaseApiResponse<unknown>>(
    PATHS.bulkRestoreFromTrash,
    { ids }
  )
}

// POST /project-management/project-attachments/bulk-permanent-delete/
export const bulkPermanentlyDeleteProjectDocuments = async (ids: string[]) => {
  return request.api.post<ProjectDocumentBulkResult, BaseApiResponse<unknown>>(
    PATHS.bulkPermanentDelete,
    { ids }
  )
}

// GET /project-management/project-attachments/:id/history/ — the full
// version chain, newest first, regardless of which version `id` names. GET
// responses are never wrapped in the {details:{data}} envelope server-side,
// so this resolves to a plain array, unlike every write below it.
export const getProjectDocumentHistory = async (id: string) => {
  return request.api.get<ProjectDocument[], BaseApiResponse<unknown>>(
    PATHS.history(id)
  )
}

// POST /project-management/project-attachments/:id/restore/ — `id` is the
// historical version to bring back; always creates a new version.
export const restoreProjectDocumentVersion = async (id: string) => {
  return request.api.post<
    BaseApiResponse<ProjectDocument>,
    BaseApiResponse<unknown>
  >(PATHS.restore(id))
}

// POST /project-management/project-attachments/:id/change-status/ — the
// manual "Change status" edit; the server enforces the transition graph and
// 400s an illegal move, `null` included: no status moves back to none.
export const changeDocumentStatus = async (
  id: string,
  status: string | null
) => {
  return request.api.post<
    BaseApiResponse<ProjectDocument>,
    BaseApiResponse<unknown>
  >(PATHS.changeStatus(id), { status })
}

// GET /project-management/project-attachments/:id/activity/ — every notable
// event across the whole version chain, newest first. Bare array (GET).
export const getProjectDocumentActivity = async (id: string) => {
  return request.api.get<DocumentActivity[], BaseApiResponse<unknown>>(
    PATHS.activity(id)
  )
}

// GET /project-management/project-attachments/:id/reviewers/ — internal
// team members on this document's project, for the reviewer picker.
export const getProjectDocumentReviewers = async (id: string) => {
  return request.api.get<
    { value: string; label: string }[],
    BaseApiResponse<unknown>
  >(PATHS.reviewers(id))
}
